import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Spinner } from '@picsio/ui';
import cn from 'classnames';

import { listProductVendors, renameProductVendor } from '../../../store/productVendors/actions';
import localization from '../../../shared/strings';
import Toast from '../../Toast/index';
import Icon from '../../Icon';
import SearchBar from '../../SearchBar/SearchBar';
import { findVendorByNormalizedTitle } from '../Vendor/vendorsDropdown/helpers';
import { getTrimmedTitleIfChanged } from './helpers';
import VendorRow from './VendorRow';
import styles from './styles.module.scss';

const l10n = localization.PRODUCT_INFO.vendor;

const VendorsManagementDialog = ({ onClose }) => {
  const dispatch = useDispatch();
  const items = useSelector((state) => state.productVendors.items);
  const isPageLoading = useSelector((state) => state.productVendors.isPageLoading);
  const isLoaded = useSelector((state) => state.productVendors.isLoaded);
  const hasMore = useSelector((state) => state.productVendors.hasMore);
  /** the last search string the store's `items`/`hasMore` are actually confirmed to reflect --
   *  used for "load more" so a freshly typed (but not-yet-fetched) `searchQuery` can never be
   *  paired with an offset that belongs to the previous query's results. */
  const confirmedSearchQuery = useSelector((state) => state.productVendors.searchQuery);
  const [, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editingOriginalTitle, setEditingOriginalTitle] = useState('');
  const [hasRenameError, setHasRenameError] = useState(false);
  const pendingSearchRef = useRef(null);

  const abortPendingSearch = useCallback(() => {
    pendingSearchRef.current?.abort?.();
    pendingSearchRef.current = null;
  }, []);

  useEffect(() => {
    /** the manage dialog always opens on the full vendor registry; never trust whatever narrowed
     *  `items` happen to be in the shared store (dropdown search, a prior in-dialog search whose
     *  `searchQuery` was cleared while `items` stayed filtered, etc.). */
    abortPendingSearch();
    const request = dispatch(listProductVendors({ search: '', from: 0 }));
    pendingSearchRef.current = request;
  }, [dispatch, abortPendingSearch]);

  useEffect(() => () => {
    abortPendingSearch();
    /** VendorsDropdown stays mounted while this dialog opens/closes -- always restore the shared
     *  canonical list on close so the dropdown is never left narrowed by in-dialog search. */
    dispatch(listProductVendors({ search: '', from: 0 }));
  }, [dispatch, abortPendingSearch]);

  /** SearchBar's `search` prop is already debounced internally (see `debounceSearch` in
   *  SearchBar.js) -- every keystroke re-queries the server, no extra debounce layer needed.
   *  A prior in-flight search is aborted before starting a new one so a slow, stale response
   *  can never land after a faster, more recent one and clobber the list with outdated results. */
  const handleSearch = useCallback((query) => {
    abortPendingSearch();
    const request = dispatch(listProductVendors({ search: query, from: 0 }));
    pendingSearchRef.current = request;
  }, [dispatch, abortPendingSearch]);

  const handleLoadMore = useCallback(() => {
    abortPendingSearch();
    const request = dispatch(listProductVendors({ search: confirmedSearchQuery, from: items.length }));
    pendingSearchRef.current = request;
  }, [dispatch, abortPendingSearch, confirmedSearchQuery, items.length]);

  const handleStartRename = useCallback((item) => {
    setEditingId(item._id);
    setEditValue(item.title);
    setEditingOriginalTitle(item.title);
    setHasRenameError(false);
  }, []);

  const handleChangeEditValue = useCallback((e) => {
    setEditValue(e.target.value);
    setHasRenameError(false);
  }, []);

  const handleCancelRename = useCallback(() => {
    setEditingId(null);
    setHasRenameError(false);
  }, []);

  const commitRename = useCallback(async () => {
    if (!editingId) return;
    const productVendorId = editingId;
    const originalTitle = editingOriginalTitle;
    const pendingEditValue = editValue;
    const titleToRename = getTrimmedTitleIfChanged(pendingEditValue, originalTitle);
    if (!titleToRename) {
      setEditingId(null);
      return;
    }

    const duplicateVendor = findVendorByNormalizedTitle(items, titleToRename);
    if (duplicateVendor && duplicateVendor._id !== productVendorId) {
      Toast(localization.PRODUCT_VENDORS.alreadyExists, { autoClose: 5000, type: 'error' });
      setHasRenameError(true);
      return;
    }

    setEditingId(null);
    /** `originalTitle` is a snapshot taken when editing started, not re-derived from the live
     *  `items` array: a concurrent search can replace `items` wholesale before the rename is
     *  committed, which would otherwise make the edited vendor unfindable and silently drop
     *  the rename. The server remains the source of truth for whether `productVendorId` is
     *  still valid -- a genuine failure is already surfaced by the rejected-match Toast below. */
    const result = await dispatch(renameProductVendor({
      productVendorId, title: titleToRename, originalTitle,
    }));
    if (renameProductVendor.fulfilled.match(result) && result.payload?.cancelled) {
      setEditingId(productVendorId);
      setEditValue(pendingEditValue);
      return;
    }
    if (renameProductVendor.fulfilled.match(result) && result.payload === null) {
      setEditingId(productVendorId);
      setEditValue(pendingEditValue);
      setHasRenameError(true);
      return;
    }
    /** the "already exists" case is already surfaced by the thunk itself via its own Toast */
    if (renameProductVendor.rejected.match(result)) {
      Toast(l10n.renameFailed, { autoClose: false, type: 'error' });
    }
  }, [dispatch, editingId, editingOriginalTitle, editValue, items]);

  const handleSave = useCallback(async () => {
    await commitRename();
    onClose();
  }, [commitRename, onClose]);

  return (
    <div className={cn('simpleDialog', styles.simpleDialogWrapper)}>
      <div className="simpleDialogUnderlayer" />
      <div className="simpleDialogBox">
        <div className="simpleDialogHeader">
          <span className="simpleDialogTitle">{l10n.manageTitle}</span>
          <span
            role="button"
            tabIndex={0}
            className="simpleDialogBtnCross"
            onClick={onClose}
            onKeyDown={onClose}
          >
            <Icon name="close" />
          </span>
        </div>
        <div className="simpleDialogContent">
          <div className="simpleDialogContentInner">
            <SearchBar
              className={styles.searchBar}
              placeholder={l10n.searchPlaceholder}
              applySearch={setSearchQuery}
              search={handleSearch}
            />
            <Choose>
              <When condition={!isLoaded}>
                <Spinner text={l10n.loadingVendors} />
              </When>
              <When condition={items.length === 0 && !confirmedSearchQuery.trim()}>
                <p className={styles.emptyState}>{l10n.emptyList}</p>
              </When>
              <When condition={items.length === 0 && confirmedSearchQuery.trim()}>
                <p className={styles.emptyState}>{l10n.emptySearchList}</p>
              </When>
              <Otherwise>
                <ul className={styles.list}>
                  {items.map((item) => (
                    <VendorRow
                      key={item._id}
                      item={item}
                      isEditing={item._id === editingId}
                      editValue={editValue}
                      hasEditError={hasRenameError}
                      onStartRename={handleStartRename}
                      onChangeEditValue={handleChangeEditValue}
                      onCommitRename={commitRename}
                      onCancelRename={handleCancelRename}
                    />
                  ))}
                  <If condition={hasMore}>
                    <li className={styles.loadMoreRow}>
                      <Choose>
                        <When condition={isPageLoading}>
                          <Spinner style={{ background: 'none' }} />
                        </When>
                        <Otherwise>
                          <Button color="secondary" size="sm" variant="text" onClick={handleLoadMore}>
                            {l10n.loadMore}
                          </Button>
                        </Otherwise>
                      </Choose>
                    </li>
                  </If>
                </ul>
              </Otherwise>
            </Choose>
          </div>
        </div>
        <div className="simpleDialogFooter">
          <Button color="secondary" size="md" variant="contained" onClick={onClose}>
            {l10n.cancel}
          </Button>
          <Button color="primary" size="md" variant="contained" onClick={handleSave}>
            {l10n.save}
          </Button>
        </div>
      </div>
    </div>
  );
};

VendorsManagementDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default VendorsManagementDialog;
